import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useUpdateQualification } from "@/features/qualifications/hooks/use-qualifications";
import type { Qualification } from "@/features/qualifications/types";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "@/features/utils/i18n";
import { QuestionFields } from "./question-field";

interface EditResponsesDialogProps {
  qualification: Qualification;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditResponsesDialog = ({
  qualification,
  open,
  onOpenChange,
}: EditResponsesDialogProps) => {
  const { t } = useTranslation("qualifications");
  const updateQualification = useUpdateQualification();
  const [responses, setResponses] = useState<Record<string, unknown>>(
    qualification.responses || {},
  );
  const [completed, setCompleted] = useState<boolean>(
    qualification.completed || false,
  );

  const questions = qualification.form_snapshot?.questions || [];

  // Reset responses and completed when qualification changes
  useEffect(() => {
    setResponses(qualification.responses || {});
    setCompleted(qualification.completed || false);
  }, [qualification]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateQualification.mutateAsync({
        qualificationId: qualification.id,
        data: { responses, completed },
      });
      toast.success(t("submissions.toasts.responses_updated"));
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("submissions.toasts.responses_update_failed"),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("submissions.edit.title")}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <QuestionFields
            questions={questions}
            responses={responses}
            onChange={setResponses}
          />

          <div className="border-t pt-6 mt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="completed">
                  {t("submissions.edit.mark_completed")}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t("submissions.edit.mark_completed_hint")}
                </p>
              </div>
              <Switch
                id="completed"
                checked={completed}
                onCheckedChange={setCompleted}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("submissions.common.cancel")}
            </Button>
            <Button type="submit" disabled={updateQualification.isPending}>
              {updateQualification.isPending
                ? t("submissions.edit.saving")
                : t("submissions.edit.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
